export type BotToggleSpec = {
  field: BotToggleField;
  label: string;
  fieldTrueDescription: string;
  fieldFalseDescription: string;
  defaultTrue: boolean;
  toastFieldTrue: string;
  toastFieldFalse: string;
  recommendation?: string;
  inverted?: boolean;
};

export type BotToggleField =
  | "injectAds"
  | "isolateUsers"
  | "enableCodeExecution"
  | "enableImageGeneration"
  | "enableWebSearch"
  | "enableBrowserUse"
  | "enableHandoff"
  | "showToolUseSpinner";

export const botToggles: BotToggleSpec[] = [
  {
    field: "injectAds",
    label: "Enable promotional mentions",
    fieldTrueDescription:
      "Bot may mention the platform when contextually appropriate.",
    fieldFalseDescription:
      "Bot will not promote the platform in conversations.",
    defaultTrue: false,
    toastFieldTrue: "Ads injection enabled",
    toastFieldFalse: "Ads injection disabled",
  },
  {
    field: "isolateUsers",
    label: "Isolate users",
    fieldTrueDescription:
      "Each conversation is independent. Bot cannot access other users' data, contacts, memory, or tasks.",
    fieldFalseDescription:
      "Bot can manage contacts, per-user memory, projects, and scheduled actions across conversations.",
    defaultTrue: true,
    toastFieldTrue: "User isolation enabled",
    toastFieldFalse: "User isolation disabled",
    recommendation:
      "Recommend enabling for support / customer-facing use cases. Do NOT recommend for personal assistant use cases where the bot needs to coordinate across conversations, manage contacts, or schedule actions.",
  },
  {
    field: "enableCodeExecution",
    label: "Enable code execution",
    fieldTrueDescription:
      "Bot can run code in sandboxes and create/manage virtual machines.",
    fieldFalseDescription: "Bot cannot run code or manage virtual machines.",
    defaultTrue: true,
    toastFieldTrue: "Code execution enabled",
    toastFieldFalse: "Code execution disabled",
  },
  {
    field: "enableImageGeneration",
    label: "Enable image generation",
    fieldTrueDescription:
      "Bot can generate images from text prompts using Google Imagen.",
    fieldFalseDescription:
      "Bot cannot generate images. The generate_image tool will be unavailable.",
    defaultTrue: true,
    toastFieldTrue: "Image generation enabled",
    toastFieldFalse: "Image generation disabled",
  },
  {
    field: "enableWebSearch",
    label: "Enable web search",
    fieldTrueDescription: "Bot can search the web.",
    fieldFalseDescription:
      "Bot cannot search the web. The search tool will be unavailable.",
    defaultTrue: true,
    toastFieldTrue: "Web search enabled",
    toastFieldFalse: "Web search disabled",
  },
  {
    field: "enableBrowserUse",
    label: "Enable browser use",
    fieldTrueDescription:
      "Bot can provision cloud browsers to navigate, interact with web pages, and take screenshots.",
    fieldFalseDescription:
      "Bot cannot use cloud browsers. The browser skill will be unavailable.",
    defaultTrue: true,
    toastFieldTrue: "Browser use enabled",
    toastFieldFalse: "Browser use disabled",
  },
  {
    field: "enableHandoff",
    label: "Enable human handoff",
    fieldTrueDescription:
      "Bot can request human handoff and admins can take over conversations live.",
    fieldFalseDescription:
      "Bot will not request human handoff. The request_human_handoff tool will be unavailable.",
    defaultTrue: false,
    toastFieldTrue: "Human handoff enabled",
    toastFieldFalse: "Human handoff disabled",
    recommendation:
      "Recommend enabling for customer support and sales bots where human agents may need to intervene or take over conversations.",
  },
  {
    field: "showToolUseSpinner",
    label: "Show tool activity spinner",
    fieldTrueDescription:
      "Users see a spinner indicator while a tool call is running.",
    fieldFalseDescription: "Tool calls run silently with no spinner indicator.",
    defaultTrue: false,
    toastFieldTrue: "Tool activity spinner enabled",
    toastFieldFalse: "Tool activity spinner disabled",
  },
];

export const botTogglesPromptSection = (updateToolName: string) =>
  [
    "# Bot Boolean Settings",
    `These settings can be toggled by users from the dashboard or set using ${updateToolName}.`,
    "",
    ...botToggles.map(({
      field,
      label,
      fieldTrueDescription,
      fieldFalseDescription,
      defaultTrue,
      recommendation,
    }) =>
      `- ${field} (default: ${defaultTrue}): "${label}". When true: ${fieldTrueDescription} When false: ${fieldFalseDescription}${
        recommendation ? ` ${recommendation}` : ""
      }`
    ),
  ].join("\n");
